import { textStyles } from '../../constants';
import { ThemedStyleSheet, useCurrentTheme } from '../../theme';

/**
 * GitHub code styles
 * Extracted from https://github.com/wooorm/starry-night/blob/main/style/both.css
 */
const palette = {
    light: {
        comment: '#6e7781',
        constant: '#0550ae',
        entity: '#8250df',
        'storage-modifier-import': '#24292f',
        'entity-tag': '#116329',
        keyword: '#cf222e',
        string: '#0a3069',
        variable: '#953800',
        'brackethighlighter-unmatched': '#82071e',
        'invalid-illegal-text': '#f6f8fa',
        'invalid-illegal-bg': '#82071e',
        'carriage-return-text': '#f6f8fa',
        'carriage-return-bg': '#cf222e',
        'string-regexp': '#116329',
        'markup-list': '#3b2300',
        'markup-heading': '#0550ae',
        'markup-italic': '#24292f',
        'markup-bold': '#24292f',
        'markup-deleted-text': '#82071e',
        'markup-deleted-bg': '#ffebe9',
        'markup-inserted-text': '#116329',
        'markup-inserted-bg': '#dafbe1',
        'markup-changed-text': '#953800',
        'markup-changed-bg': '#ffd8b5',
        'markup-ignored-text': '#eaeef2',
        'markup-ignored-bg': '#0550ae',
        'meta-diff-range': '#8250df',
        'brackethighlighter-angle': '#57606a',
        'sublimelinter-gutter-mark': '#8c959f',
        'constant-other-reference-link': '#0a3069',
    },
    dark: {
        comment: '#8b949e',
        constant: '#79c0ff',
        entity: '#d2a8ff',
        'storage-modifier-import': '#c9d1d9',
        'entity-tag': '#7ee787',
        keyword: '#ff7b72',
        string: '#a5d6ff',
        variable: '#ffa657',
        'brackethighlighter-unmatched': '#f85149',
        'invalid-illegal-text': '#f0f6fc',
        'invalid-illegal-bg': '#8e1519',
        'carriage-return-text': '#f0f6fc',
        'carriage-return-bg': '#b62324',
        'string-regexp': '#7ee787',
        'markup-list': '#f2cc60',
        'markup-heading': '#1f6feb',
        'markup-italic': '#c9d1d9',
        'markup-bold': '#c9d1d9',
        'markup-deleted-text': '#ffdcd7',
        'markup-deleted-bg': '#67060c',
        'markup-inserted-text': '#aff5b4',
        'markup-inserted-bg': '#033a16',
        'markup-changed-text': '#ffdfb6',
        'markup-changed-bg': '#5a1e02',
        'markup-ignored-text': '#c9d1d9',
        'markup-ignored-bg': '#1158c7',
        'meta-diff-range': '#d2a8ff',
        'brackethighlighter-angle': '#8b949e',
        'sublimelinter-gutter-mark': '#484f58',
        'constant-other-reference-link': '#a5d6ff',
    },
};

/**
 * Return the styles to highlight code in the current theme.
 */
export function useCodeHighlightingStyles() {
    const [, codeHighlightStyles] = useCurrentTheme(codeHighlightThemedStyles);
    return codeHighlightStyles;
}

const codeHighlightThemedStyles = ThemedStyleSheet.create({
    // Comment
    comment: (_theme, variantsValue) => ({ color: palette[variantsValue.color].comment }),
    prolog: (_theme, variantsValue) => ({ color: palette[variantsValue.color].comment }),
    doctype: (_theme, variantsValue) => ({ color: palette[variantsValue.color].constant }),
    cdata: (_theme, variantsValue) => ({ color: palette[variantsValue.color].comment }),
    // Punctuation
    punctuation: () => ({}),
    namespace: { opacity: 0.7 },
    deleted: (_theme, variantsValue) => ({
        color: palette[variantsValue.color]['markup-deleted-text'],
        backgroundColor: palette[variantsValue.color]['markup-deleted-bg'],
    }),
    inserted: (_theme, variantsValue) => ({
        color: palette[variantsValue.color]['markup-inserted-text'],
        backgroundColor: palette[variantsValue.color]['markup-inserted-bg'],
    }),
    italic: textStyles.Italic,
    bold: textStyles.Bold,
    // Operator
    operator: (_theme, variantsValue) => ({ color: palette[variantsValue.color].constant }),
    boolean: (_theme, variantsValue) => ({ color: palette[variantsValue.color].constant }),
    number: (_theme, variantsValue) => ({ color: palette[variantsValue.color].constant }),
    property: (_theme, variantsValue) => ({ color: palette[variantsValue.color].constant }),
    // tag
    tag: (_theme, variantsValue) => ({ color: palette[variantsValue.color]['entity-tag'] }),
    'attr-name': (_theme, variantsValue) => ({ color: palette[variantsValue.color].constant }),
    'attr-value': (_theme, variantsValue) => ({ color: palette[variantsValue.color].string }),
    string: (_theme, variantsValue) => ({ color: palette[variantsValue.color].string }),
    entity: (_theme, variantsValue) => ({ color: palette[variantsValue.color].string }),
    url: (_theme, variantsValue) => ({ color: palette[variantsValue.color].string }),
    statement: (_theme, variantsValue) => ({ color: palette[variantsValue.color].string }),
    regex: (_theme, variantsValue) => ({ color: palette[variantsValue.color].string }),
    atrule: (_theme, variantsValue) => ({ color: palette[variantsValue.color].string }),
    placeholder: (_theme, variantsValue) => ({ color: palette[variantsValue.color].string }),
    variable: (_theme, variantsValue) => ({ color: palette[variantsValue.color].string }),
    selector: (_theme, variantsValue) => ({ color: palette[variantsValue.color].string }),
    // Keyword
    keyword: (_theme, variantsValue) => ({ color: palette[variantsValue.color].keyword }),
    builtin: (_theme, variantsValue) => ({ color: palette[variantsValue.color].keyword }),
    control: (_theme, variantsValue) => ({ color: palette[variantsValue.color].keyword }),
    directive: (_theme, variantsValue) => ({ color: palette[variantsValue.color].keyword }),
    unit: (_theme, variantsValue) => ({ color: palette[variantsValue.color].keyword }),
    constant: (_theme, variantsValue) => ({ color: palette[variantsValue.color].constant }),
    // Function and class names
    function: (_theme, variantsValue) => ({ color: palette[variantsValue.color].entity }),
    'class-name': (_theme, variantsValue) => ({ color: palette[variantsValue.color].variable }),
    // Important
    important: (_theme, variantsValue) => ({
        color: palette[variantsValue.color].keyword,
    }),
});
